import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Auth } from 'aws-amplify';
import { isEmpty } from 'lodash';
import moment, { unix } from 'moment';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { AuthService } from './auth.service';

import { environment } from '@env/environment';
import { serialize } from '@shared/models/base-helper';
import * as fromApp from '@store';
import { logout, setToken } from '@store/auth/auth.actions';
import { ToastService } from '@shared/services/toast.service';
import { ToastLevel } from '@shared/models/toast-message.module';

/**
 * Add token to all HttpClient request headers
 */
@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  readonly authService: AuthService = this.injector.get(AuthService);

  constructor(
    readonly injector: Injector, readonly store: Store<fromApp.AppState>, readonly toastService: ToastService) {}

  setRequestHeaders(request: HttpRequest<any>, token: string, actAsUser: string): HttpRequest<any> {
    if (!isEmpty(token)) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    if (actAsUser) {
      request = request.clone({
        setHeaders: {
          'X-FC-ActAs': actAsUser,
        },
      });
    }
    return request;
  }

  logout() {
    this.store.dispatch(logout());
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.startsWith(environment.apiUrl)) {
      request = request.clone({ body: serialize(request.body) });
      if (this.authService !== null) {
        const token = this.authService.getToken();
        const exp = this.authService.getExp();
        const actAsUser = this.authService.getActAsUser();
        if (request.headers.get('X-FC-Noauth')) {
          // Continue if no auth is required
          return next.handle(request);
        } else if (exp && unix(exp).isBefore(moment()) && environment.env !== 'dev' && environment.env !== 'ci') {
          // If token has expired, request a new token from Cognito
          return from(Auth.currentSession()).pipe(
            mergeMap(session => {
              // Set the refreshed token and update the expiration date
              const idToken = session.getIdToken();
              this.authService.token = idToken.getJwtToken();
              this.authService.exp = idToken.getExpiration();
              this.store.dispatch(
                setToken({
                  idToken: this.authService.token,
                  exp: this.authService.exp,
                })
              );
              return next.handle(this.setRequestHeaders(request, this.authService.token, actAsUser));
            }), catchError(error => {
              // Log the user out if there was an error refreshing the session token
              console.log(`There was an error refreshing the session token: ${error}`);
              // Don't display the toast if there is already a session expired message active
              if (!this.toastService.getMessages()
                .some(msg => msg.level === ToastLevel.WARN && msg.message.includes("Session expired"))) {
                this.toastService.sendMessage("Session expired, please log in again.", ToastLevel.WARN);
                this.logout();
              }
              return EMPTY
            })
          );
        } else if (!isEmpty(token)) {
          return next.handle(this.setRequestHeaders(request, token, actAsUser));
        }
      }
    } else {
      return next.handle(request);
    }
  }
}

